import { useEffect, useRef, useState } from 'react';
import { Play, Pause, Volume2, VolumeX, RotateCcw } from 'lucide-react';

// 5 photos from the retreat, ordered to tell an arc
const SLIDES = [
  {
    src: 'https://customer-assets-4nw71qhi.emergentagent.net/job_analytics-live-1/artifacts/q2to1dje_PXL_20260724_122022852.webp',
    title: 'Panama.',
    subtitle: 'The Westin Playa Bonita',
    ken: 'zoom-in-slow',
  },
  {
    src: 'https://customer-assets-4nw71qhi.emergentagent.net/job_analytics-live-1/artifacts/wl44vrnh_PXL_20260724_172901118.webp',
    title: 'We showed up.',
    subtitle: 'Brothers, in the flesh',
    ken: 'pan-right',
  },
  {
    src: 'https://customer-assets-4nw71qhi.emergentagent.net/job_analytics-live-1/artifacts/g7mkkk7i_PXL_20260725_193333031.webp',
    title: 'We explored.',
    subtitle: 'A country. A history. A moment.',
    ken: 'zoom-out-slow',
  },
  {
    src: 'https://customer-assets-4nw71qhi.emergentagent.net/job_analytics-live-1/artifacts/nznk02k7_PXL_20260725_185730181.webp',
    title: 'We honored the giants.',
    subtitle: 'Marcus Garvey \u00b7 Panama City',
    ken: 'pan-left',
  },
  {
    src: 'https://customer-assets-4nw71qhi.emergentagent.net/job_analytics-live-1/artifacts/7skeeo05_PXL_20260726_004540194.MP.webp',
    title: 'We reset.',
    subtitle: 'Together',
    ken: 'zoom-in',
  },
];

const SLIDE_MS = 6000;
const TOTAL_MS = SLIDES.length * SLIDE_MS;

// Royalty-free Afrobeats-style track (Pixabay, self-hosted for reliable playback).
// Replace /audio/reset-promo.mp3 with any other track to swap music.
const MUSIC_URL = '/audio/reset-promo.mp3';

export default function PromoPage() {
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(true);
  const [done, setDone] = useState(false);
  const audioRef = useRef(null);
  const startedAtRef = useRef(Date.now());
  const rafRef = useRef(null);
  const [progress, setProgress] = useState(0);

  // Advance slides
  useEffect(() => {
    if (!playing || done) return;
    const t = setTimeout(() => {
      if (index < SLIDES.length - 1) {
        setIndex((i) => i + 1);
      } else {
        setDone(true);
      }
    }, SLIDE_MS);
    return () => clearTimeout(t);
  }, [index, playing, done]);

  // Progress bar loop
  useEffect(() => {
    if (!playing || done) return;
    startedAtRef.current = Date.now() - progress * TOTAL_MS;
    const tick = () => {
      const elapsed = Date.now() - startedAtRef.current;
      const pct = Math.min(1, elapsed / TOTAL_MS);
      setProgress(pct);
      if (pct < 1 && playing && !done) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => rafRef.current && cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, done]);

  // Audio play/pause + mute wiring
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    el.muted = muted;
    if (playing && !done) {
      el.play().catch(() => {
        /* autoplay blocked when unmuted, user will tap */
      });
    } else {
      el.pause();
    }
  }, [playing, muted, done]);

  const restart = () => {
    setIndex(0);
    setDone(false);
    setProgress(0);
    setPlaying(true);
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    }
  };

  const current = SLIDES[index];

  return (
    <div
      className="min-h-screen bg-black text-white overflow-hidden relative"
      data-testid="promo-page"
    >
      {/* Background music */}
      <audio ref={audioRef} src={MUSIC_URL} loop preload="auto" />

      {/* Slides */}
      <div className="fixed inset-0">
        {SLIDES.map((slide, i) => (
          <div
            key={i}
            className={`absolute inset-0 transition-opacity duration-[1400ms] ease-in-out ${
              i === index && !done ? 'opacity-100' : 'opacity-0'
            }`}
            data-testid={`slide-${i}`}
            aria-hidden={i !== index}
          >
            {/* Image with Ken Burns effect */}
            <div
              className={`absolute inset-0 bg-cover bg-center will-change-transform ${
                i === index ? `ken-${slide.ken}` : ''
              }`}
              style={{ backgroundImage: `url(${slide.src})` }}
            />
            {/* Dark gradient overlay for text legibility */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/10 to-black/80" />
            {/* Grain vignette */}
            <div className="absolute inset-0 bg-radial-vignette" />
          </div>
        ))}

        {/* End card */}
        {done && (
          <div
            className="absolute inset-0 bg-black flex items-center justify-center animate-fade-in"
            data-testid="promo-end-card"
          >
            <div className="text-center px-6 max-w-2xl">
              <p className="text-[#D4AF37] font-manrope uppercase tracking-[0.4em] text-xs font-bold mb-6">
                The Reset &middot; Panama
              </p>
              <h2 className="font-cormorant text-5xl md:text-7xl font-medium text-white leading-[1.05] mb-6">
                A moment <span className="italic text-[#D4AF37]">in our story.</span>
              </h2>
              <p className="text-zinc-300 font-manrope text-lg md:text-xl mb-10">
                For the brothers who showed up &middot; and the ones still to come.
              </p>
              <button
                onClick={restart}
                className="inline-flex items-center gap-2 border border-white/20 text-white px-8 py-4 rounded-full font-manrope font-medium hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
                data-testid="promo-replay-btn"
              >
                <RotateCcw className="w-4 h-4" /> Watch again
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Overlay text */}
      {!done && (
        <div className="relative z-10 min-h-screen flex flex-col justify-end px-6 pb-24 md:pb-32 pointer-events-none">
          <div key={index} className="max-w-3xl animate-slide-up">
            <p
              className="text-[#D4AF37] font-manrope uppercase tracking-[0.35em] text-[11px] md:text-xs font-bold mb-4"
              data-testid="slide-eyebrow"
            >
              The Reset &middot; A moment in Panama
            </p>
            <h1
              className="font-cormorant text-5xl md:text-8xl font-medium leading-[1.05] mb-3"
              data-testid={`slide-title-${index}`}
            >
              {current.title}
            </h1>
            <p className="text-zinc-200 font-manrope text-lg md:text-2xl font-light">
              {current.subtitle}
            </p>
          </div>
        </div>
      )}

      {/* Progress bar */}
      <div className="fixed top-0 left-0 right-0 h-0.5 bg-white/10 z-20">
        <div
          className="h-full bg-[#D4AF37] transition-[width] duration-100 ease-linear"
          style={{ width: `${progress * 100}%` }}
          data-testid="promo-progress"
        />
      </div>

      {/* Slide dots */}
      <div className="fixed top-5 left-1/2 -translate-x-1/2 z-20 flex gap-1.5">
        {SLIDES.map((_, i) => (
          <span
            key={i}
            className={`h-1 rounded-full transition-all ${
              i < index || done
                ? 'w-6 bg-[#D4AF37]'
                : i === index
                ? 'w-10 bg-white'
                : 'w-6 bg-white/20'
            }`}
          />
        ))}
      </div>

      {/* Controls */}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 bg-black/60 backdrop-blur-md border border-white/10 rounded-full px-2 py-2">
        <button
          onClick={() => setPlaying((p) => !p)}
          className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
          data-testid="promo-play-btn"
          aria-label={playing ? 'Pause' : 'Play'}
        >
          {playing && !done ? (
            <Pause className="w-4 h-4" />
          ) : (
            <Play className="w-4 h-4 ml-0.5" />
          )}
        </button>
        <button
          onClick={() => setMuted((m) => !m)}
          className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
          data-testid="promo-mute-btn"
          aria-label={muted ? 'Unmute' : 'Mute'}
        >
          {muted ? (
            <VolumeX className="w-4 h-4" />
          ) : (
            <Volume2 className="w-4 h-4 text-[#D4AF37]" />
          )}
        </button>
        {muted && (
          <span className="text-zinc-300 font-manrope text-xs px-3 whitespace-nowrap">
            Tap for sound
          </span>
        )}
      </div>

      {/* Ken Burns + animations */}
      <style>{`
        @keyframes ken-zoom-in {
          from { transform: scale(1.02) translate(0, 0); }
          to   { transform: scale(1.18) translate(-2%, -2%); }
        }
        @keyframes ken-zoom-in-slow {
          from { transform: scale(1.02); }
          to   { transform: scale(1.14); }
        }
        @keyframes ken-zoom-out-slow {
          from { transform: scale(1.14); }
          to   { transform: scale(1.02); }
        }
        @keyframes ken-pan-right {
          from { transform: scale(1.12) translateX(-3%); }
          to   { transform: scale(1.12) translateX(3%); }
        }
        @keyframes ken-pan-left {
          from { transform: scale(1.12) translateX(3%); }
          to   { transform: scale(1.12) translateX(-3%); }
        }
        .ken-zoom-in       { animation: ken-zoom-in ${SLIDE_MS + 800}ms ease-out forwards; }
        .ken-zoom-in-slow  { animation: ken-zoom-in-slow ${SLIDE_MS + 800}ms ease-out forwards; }
        .ken-zoom-out-slow { animation: ken-zoom-out-slow ${SLIDE_MS + 800}ms ease-out forwards; }
        .ken-pan-right     { animation: ken-pan-right ${SLIDE_MS + 800}ms ease-in-out forwards; }
        .ken-pan-left      { animation: ken-pan-left ${SLIDE_MS + 800}ms ease-in-out forwards; }

        @keyframes slide-up {
          from { transform: translateY(28px); opacity: 0; }
          to   { transform: translateY(0); opacity: 1; }
        }
        .animate-slide-up { animation: slide-up 900ms cubic-bezier(0.22, 1, 0.36, 1) both; }

        @keyframes fade-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        .animate-fade-in { animation: fade-in 900ms ease-out both; }

        .bg-radial-vignette {
          background: radial-gradient(circle at center, transparent 40%, rgba(0,0,0,0.55) 100%);
        }
      `}</style>
    </div>
  );
}
